import api from "./ApiService";

// The three states an account can be in. They need opposite things said to
// them, which is the whole reason this exists: the old endpoint answered both
// "never subscribed" and "lapsed" with the same failure, so a brand new account
// was told its subscription had expired.
export const NONE = "none";
export const ACTIVE = "active";
export const EXPIRED = "expired";

const UNKNOWN = { state: NONE, daysRemaining: 0, expiryDate: null };

/**
 * Current subscription state for the signed-in user, read from the same rule
 * the API uses to allow or refuse writes, so the screen cannot promise
 * something the server will then refuse.
 */
export const fetchSubscriptionStatus = async () => {
  try {
    const { data } = await api.get("user-subscription/status");

    if (data?.statusCode !== 200 || !data?.data) {
      return UNKNOWN;
    }

    return {
      state: data.data.state ?? NONE,
      daysRemaining: Number(data.data.daysRemaining ?? 0),
      expiryDate: data.data.expiryDate ?? null,
      isFree: Boolean(data.data.isFree),
    };
  } catch (error) {
    // Treated as "not subscribed" rather than throwing: the caller uses this to
    // decide what to offer, and a failed check should not leave the page with
    // no call to action at all.
    return UNKNOWN;
  }
};

/**
 * The banner text for a state. Returns null when there is nothing worth saying
 * — an account with a month left does not need a notice on every screen.
 */
export const subscriptionMessage = ({ state, daysRemaining }) => {
  if (state === NONE) {
    return "Start your free trial to create and save projects.";
  }

  if (state === EXPIRED) {
    return "Your subscription has expired. Renew to create and save projects.";
  }

  if (state === ACTIVE) {
    // Always shown while a subscription runs, not only near the end. Someone on
    // a 30-day trial has no other way to find out how much of it is left.
    if (daysRemaining <= 0) {
      return null;
    }

    return daysRemaining === 1
      ? "Your subscription expires today."
      : `${daysRemaining} days remaining on your subscription.`;
  }

  return null;
};

export const canCreateProjects = ({ state }) => state === ACTIVE;
